import { createSlice } from "@reduxjs/toolkit";

import * as api from "../../services/planBoardApi";

/**
 * planBoard: the v6 plan board's client state (master plan sections 5.4 and 6).
 * The legacy `planTasks` slice is not touched and not read.
 *
 * TWO LAYERS.
 *   `server`   the last board the server confirmed, in COMMITTED ORDER. It only
 *              changes through `ingest` (an event or a mutation response at
 *              exactly known+1) or a snapshot whose version is at least the
 *              current one. `boardVersion` is its version ("known").
 *   `pending`  the viewer's own requests, keyed by the request key PlanBoard
 *              generated. They are overlays computed by `selectBoardView`, never
 *              written into `server`, so a snapshot can replace `server` without
 *              erasing work the viewer is still waiting on.
 *
 * REALTIME RULES (5.4), all in `ingest`:
 *   - an event without `sessionId`, or for another session, squad or capsule,
 *     is dropped;
 *   - while a snapshot fetch is in flight every event is buffered;
 *   - at or below known: ignored; exactly known+1: applied and known advances;
 *     ahead of known+1: nothing is applied and a refetch is requested;
 *   - a snapshot is applied only if its version is at least known; buffered
 *     events are then applied in version order by the same rules.
 * A successful mutation response is ingested as the event it would have been,
 * so it follows the same rules (R2 finding 6).
 *
 * Personal data (the viewer's completion, drafts) arrives only on the snapshot
 * and is never read from the squad channel.
 */

export const V6_ONLY_EVENT_TYPES = [
  "plan_idea_created",
  "plan_idea_updated",
  "plan_idea_deleted",
  "plan_round_started",
  "plan_round_skipped",
];

export const UPGRADE_REQUIRED = "plan_board_v6_upgrade_required";
export const V6_DISABLED = "plan_board_v6_disabled";
export const V6_PAUSED = "plan_board_v6_paused";

/** Keyed operations (5.2): the request key is sent as `client_request_id`. */
const KEYED = new Set([
  "addIdea",
  "convert",
  "another",
  "addTask",
  "claim",
  "release",
  "restore",
  "finalize",
]);

export const initialState = {
  scope: null, // { sessionId, squadId, capsuleId, viewerId }
  status: "idle", // idle | loading | ready | error
  loadError: null, // { status, code, message }
  server: null,
  boardVersion: null,
  fetchInFlight: false,
  buffer: [],
  needsRefetch: false,
  pending: {},
  conflicts: {},
  claimLost: {},
  // taskId -> error code: a release the server refused (plan UX v7, D3).
  releaseRefused: {},
  // taskId -> perfNow() of the viewer's last successful draft save (D4).
  draftSavedAt: {},
  clock: null, // { serverMs, perfMs }
  paused: false,
  upgradeRequired: false,
  tempSeq: 0,
  fetchFailures: 0,
  lastSnapshotAt: null,
  // Which board each squad:capsule resolved to, for the life of the page, so
  // the Plan step probes once (F1 review B1). Kept through scope changes.
  modes: {},
};

/** A failed reconciliation is retried on its own, this many times at most. */
export const MAX_FETCH_FAILURES = 5;

/* ---------------- value helpers ---------------- */

const num = (v) =>
  v === null || v === undefined || v === "" ? null : Number(v);

/** Server time to epoch milliseconds: ISO string, epoch seconds or ms. */
export function toMs(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === "string" && /^\d+(\.\d+)?$/.test(value)) {
    return toMs(Number(value));
  }
  // A database timestamp without a zone ("2026-09-26 14:03:05") is the
  // server's UTC. Browsers disagree on it (V8 reads local time, Safari
  // returns NaN), so it is made explicit before parsing.
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value)
  ) {
    return Date.parse(`${value.replace(" ", "T")}Z`);
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

const versionOf = (payload) => {
  const v = num(payload?.board_version);
  return Number.isFinite(v) ? v : null;
};

const sameId = (a, b) =>
  a !== null && a !== undefined && Number(a) === Number(b);

/** A draft names its task as `task_id` (or the table's own column name). */
export const draftTaskId = (d) => d?.task_id ?? d?.planning_session_task_id;

/** The viewer's own draft for a task, or null. */
export const draftFor = (pb, taskId) =>
  (pb?.server?.viewer?.definition_drafts || []).find((d) =>
    sameId(draftTaskId(d), taskId),
  ) || null;

/** Is this socket message a v6 board event? Legacy events never carry a version. */
export const isV6BoardEvent = (message) =>
  typeof message?.type === "string" &&
  message.type.startsWith("plan_") &&
  ((message.board_version !== undefined && message.board_version !== null) ||
    V6_ONLY_EVENT_TYPES.includes(message.type));

/** Does a GET v6/board body look like the 5.2 snapshot? */
export const isSnapshot = (data) =>
  Boolean(
    data &&
      typeof data === "object" &&
      versionOf(data) !== null &&
      data.session &&
      typeof data.session === "object" &&
      Array.isArray(data.ideas) &&
      Array.isArray(data.tasks),
  );

/* ---------------- applying one event to `server` ---------------- */

const upsert = (list, entity) => {
  const i = list.findIndex((x) => sameId(x.id, entity.id));
  if (i === -1) list.push(entity);
  else list[i] = { ...list[i], ...entity };
};
const removeById = (list, id) => {
  const i = list.findIndex((x) => sameId(x.id, id));
  if (i !== -1) list.splice(i, 1);
};
const findById = (list, id) => list.find((x) => sameId(x.id, id));

const ROUND_FIELDS = [
  "round_started_at",
  "round_started_by",
  "round_skipped_at",
];

/**
 * Applies one board event to an immer draft of `server`. Returns false when the
 * event cannot be applied from its own payload, which callers treat as a gap.
 */
function applyEvent(server, message) {
  const entityTask = message.task && typeof message.task === "object";
  const entityIdea = message.idea && typeof message.idea === "object";
  switch (message.type) {
    case "plan_idea_created":
    case "plan_idea_updated":
      if (!entityIdea) return false;
      upsert(server.ideas, message.idea);
      return true;
    case "plan_idea_deleted": {
      const id = message.ideaId ?? message.idea?.id;
      if (id === undefined || id === null) return false;
      removeById(server.ideas, id);
      return true;
    }
    case "plan_task_created":
      if (!entityTask) return false;
      upsert(server.tasks, message.task);
      return true;
    case "plan_task_deleted": {
      const id = message.taskId ?? message.task?.id;
      if (id === undefined || id === null) return false;
      // Deleting a first conversion gives its idea back to the board (5.1).
      // The snapshot leaves out ideas a live first conversion represents, so
      // when this board does not hold the returned idea it cannot draw it:
      // that is a gap, found before anything changes, and the board reloads.
      const restored = message.restored_idea_id;
      if (
        restored !== undefined &&
        restored !== null &&
        !findById(server.ideas, restored)
      ) {
        return false;
      }
      removeById(server.tasks, id);
      return true;
    }
    case "plan_task_updated":
    case "plan_task_claimed":
    case "plan_task_unclaimed":
    case "plan_task_finalized": {
      if (entityTask) {
        // An update never creates: a partial payload for a task this board
        // does not hold is a gap, not a new card.
        if (!findById(server.tasks, message.task.id)) return false;
        upsert(server.tasks, message.task);
        return true;
      }
      const task = findById(server.tasks, message.taskId);
      if (!task) return false;
      if (message.type === "plan_task_updated") {
        if (typeof message.title !== "string") return false;
        task.title = message.title;
      } else if (message.type === "plan_task_claimed") {
        if (message.userId === undefined) return false;
        task.assigned_to_user_id = message.userId;
      } else if (message.type === "plan_task_unclaimed") {
        task.assigned_to_user_id = null;
      } else {
        task.locked = true;
      }
      return true;
    }
    case "plan_round_started":
    case "plan_round_skipped": {
      const source =
        message.session && typeof message.session === "object"
          ? message.session
          : message;
      const present = ROUND_FIELDS.filter((f) => f in source);
      if (!present.length) return false;
      present.forEach((f) => {
        server.session[f] = source[f];
      });
      return true;
    }
    case "plan_phase_changed": {
      const phase = message.phase ?? message.session?.phase;
      if (!phase) return false;
      server.session.phase = phase;
      return true;
    }
    default:
      return false;
  }
}

/* ---------------- ingest (the 5.4 rules) ---------------- */

const matchesScope = (scope, message) => {
  if (
    !scope ||
    message?.sessionId === undefined ||
    message.sessionId === null
  ) {
    return false;
  }
  if (!sameId(message.sessionId, scope.sessionId)) return false;
  if (
    message.squadId !== undefined &&
    message.squadId !== null &&
    scope.squadId !== null &&
    scope.squadId !== undefined &&
    !sameId(message.squadId, scope.squadId)
  ) {
    return false;
  }
  if (
    message.capsuleId !== undefined &&
    message.capsuleId !== null &&
    scope.capsuleId !== null &&
    scope.capsuleId !== undefined &&
    !sameId(message.capsuleId, scope.capsuleId)
  ) {
    return false;
  }
  return true;
};

/** Settled requests whose result the board now holds are forgotten. */
function prune(state) {
  const known = state.boardVersion;
  Object.keys(state.pending).forEach((key) => {
    const e = state.pending[key];
    if (e.status === "settled" && (known === null || e.awaitVersion <= known)) {
      delete state.pending[key];
    }
  });
}

/** One event against known, with no buffering: apply, ignore or ask to refetch. */
function applyInOrder(state, message) {
  const v = versionOf(message);
  if (v === null || !state.server || state.boardVersion === null) {
    state.needsRefetch = true;
    return;
  }
  if (v <= state.boardVersion) return;
  if (v !== state.boardVersion + 1) {
    state.needsRefetch = true;
    return;
  }
  if (!applyEvent(state.server, message)) {
    state.needsRefetch = true;
    return;
  }
  state.boardVersion = v;
}

function ingest(state, message) {
  if (state.fetchInFlight) {
    state.buffer.push(message);
    return;
  }
  applyInOrder(state, message);
  prune(state);
}

function drainBuffer(state) {
  const buffered = state.buffer
    .slice()
    .sort((a, b) => (versionOf(a) ?? -1) - (versionOf(b) ?? -1));
  state.buffer = [];
  buffered.forEach((m) => applyInOrder(state, m));
  prune(state);
}

/* ---------------- mutation responses as events ---------------- */

const OP_EVENT = {
  addIdea: "plan_idea_created",
  editIdea: "plan_idea_updated",
  deleteIdea: "plan_idea_deleted",
  convert: "plan_task_created",
  another: "plan_task_created",
  addTask: "plan_task_created",
  editTask: "plan_task_updated",
  deleteTask: "plan_task_deleted",
  restore: "plan_task_created",
  claim: "plan_task_claimed",
  release: "plan_task_unclaimed",
  startRound: "plan_round_started",
  skipRound: "plan_round_skipped",
  openClaim: "plan_phase_changed",
  finalize: "plan_task_finalized",
};

const IDEA_OPS = new Set(["addIdea", "editIdea", "deleteIdea"]);
const SESSION_OPS = new Set(["startRound", "skipRound", "openClaim"]);

/** The entity a success response carries, read tolerantly. */
export function responseEntity(op, data) {
  if (!data || typeof data !== "object") return null;
  if (SESSION_OPS.has(op)) return data.session || data;
  if (IDEA_OPS.has(op))
    return data.idea || (data.id !== undefined ? data : null);
  return data.task || (data.id !== undefined ? data : null);
}

function responseAsEvent(state, entry, data) {
  const entity = responseEntity(entry.op, data);
  const message = {
    type: OP_EVENT[entry.op],
    sessionId: state.scope.sessionId,
    board_version: data?.board_version,
  };
  if (SESSION_OPS.has(entry.op)) {
    message.session = entity || {};
    if (entry.op === "openClaim") message.phase = entity?.phase ?? "claim";
  } else if (entry.op === "deleteIdea") {
    message.ideaId = entity?.id ?? entry.args.ideaId;
  } else if (entry.op === "deleteTask") {
    message.taskId = entity?.id ?? entry.args.taskId;
    message.restored_idea_id =
      data?.restored_idea_id ?? entity?.restored_idea_id;
  } else if (IDEA_OPS.has(entry.op)) {
    message.idea = entity;
  } else {
    message.task = entity;
  }
  return { message, entity };
}

/* ---------------- the slice ---------------- */

const planBoardSlice = createSlice({
  name: "planBoard",
  initialState,
  reducers: {
    /** A different session is a different board: nothing carries over. */
    scopeSet(state, { payload }) {
      const same =
        state.scope && sameId(state.scope.sessionId, payload.sessionId);
      if (same) {
        state.scope = { ...state.scope, ...payload };
        return;
      }
      return { ...initialState, modes: state.modes, scope: { ...payload } };
    },
    scopeCleared(state) {
      return { ...initialState, modes: state.modes };
    },
    modeResolved(state, { payload: { key, mode } }) {
      state.modes[key] = mode;
    },
    modeForgotten(state, { payload: key }) {
      delete state.modes[key];
    },
    /**
     * The board closed: requests that are not in flight (failed, or waiting
     * while offline) are dropped, so a closed board never comes back with an
     * old "couldn't save" (F1 review A7). Requests in flight keep their
     * entries so their answers still land.
     */
    pendingSettledOnClose(state) {
      Object.keys(state.pending).forEach((key) => {
        const e = state.pending[key];
        if (e.status === "failed" || e.status === "queued") {
          delete state.pending[key];
        }
      });
    },
    fetchStarted(state) {
      state.fetchInFlight = true;
      state.needsRefetch = false;
      if (!state.server) state.status = "loading";
    },
    snapshotReceived(state, { payload }) {
      const { sessionId, data, clock } = payload;
      if (!state.scope || !sameId(sessionId, state.scope.sessionId)) return;
      state.fetchInFlight = false;
      const v = versionOf(data);
      if (!isSnapshot(data)) {
        state.status = state.server ? "ready" : "error";
        if (!state.server) {
          state.loadError = { status: 200, code: "bad_snapshot", message: "" };
        }
        drainBuffer(state);
        return;
      }
      if (state.boardVersion === null || v >= state.boardVersion) {
        // Plan UX v7 D4: the snapshot's viewer is authoritative, except that
        // a draft this viewer saved AFTER this snapshot's request started is
        // kept over the snapshot's entry for that task. One clock: both
        // times come from perfNow(). Every other entry, and every other
        // viewer field, is the snapshot's.
        const startedAt = payload.requestStartedAt;
        const localDrafts = state.server?.viewer?.definition_drafts || [];
        const viewer = { ...(data.viewer || {}) };
        if (typeof startedAt === "number") {
          const newer = localDrafts.filter(
            (d) =>
              (state.draftSavedAt[draftTaskId(d)] ?? -Infinity) > startedAt,
          );
          if (newer.length) {
            const keep = new Set(newer.map((d) => String(draftTaskId(d))));
            viewer.definition_drafts = [
              ...(Array.isArray(viewer.definition_drafts)
                ? viewer.definition_drafts
                : []
              ).filter((d) => !keep.has(String(draftTaskId(d)))),
              ...newer,
            ];
          }
        }
        state.server = {
          session: { ...data.session },
          ideas: data.ideas.slice(),
          tasks: data.tasks.slice(),
          members: Array.isArray(data.members) ? data.members.slice() : [],
          deliverable: data.deliverable || null,
          viewer,
          level: data.level || {},
        };
        state.boardVersion = v;
        // Fresh only when this answer was applied: an older one reconciled
        // nothing (CodeRabbit follow-up on PR #464).
        if (typeof payload.at === "number") state.lastSnapshotAt = payload.at;
      }
      if (clock) state.clock = clock;
      state.status = "ready";
      state.loadError = null;
      state.fetchFailures = 0;
      drainBuffer(state);
    },
    fetchFailed(state, { payload }) {
      const { sessionId, error } = payload;
      if (!state.scope || !sameId(sessionId, state.scope.sessionId)) return;
      state.fetchInFlight = false;
      if (!state.server) {
        state.status = "error";
        state.loadError = error;
      }
      if (error?.code === V6_PAUSED) state.paused = true;
      state.fetchFailures += 1;
      // The board is behind until a fetch lands, so it tries again (the
      // container spaces the attempts out), then stops and waits for the next
      // trigger: an event gap, a refocus, a reconnect, or Retry.
      state.needsRefetch = state.fetchFailures < MAX_FETCH_FAILURES;
      drainBuffer(state);
    },
    boardEventReceived(state, { payload: message }) {
      if (!matchesScope(state.scope, message)) return;
      ingest(state, message);
    },
    /** Socket reconnect or tab refocus: reconcile with the server (5.4). */
    /**
     * Reconcile with the server. Optional triggers (refocus, reopen) pass
     * `freshWithinMs`: a snapshot applied that recently makes the fetch
     * redundant, so it is skipped (no double load on open). Gaps and socket
     * reconnects call it bare and always refetch.
     */
    refetchRequested(state, { payload } = {}) {
      if (!state.scope) return;
      const within = payload?.freshWithinMs;
      if (
        within &&
        state.lastSnapshotAt !== null &&
        payload.now - state.lastSnapshotAt < within
      ) {
        return;
      }
      state.needsRefetch = true;
    },
    mutationQueued(state, { payload }) {
      const { key, op, args, meta = {} } = payload;
      // A new try at letting go forgets the last refusal (D3).
      if (op === "release" && args?.taskId !== undefined) {
        delete state.releaseRefused[args.taskId];
      }
      state.tempSeq += 1;
      state.pending[key] = {
        op,
        args,
        kind: meta.kind || op,
        status: "saving",
        seq: state.tempSeq,
        tempId: -state.tempSeq,
        ideaId: meta.ideaId ?? null,
        taskId: meta.taskId ?? null,
        draftText: meta.draftText ?? null,
        applied: meta.applied !== false,
        visible: meta.visible !== false,
      };
      // Choosing to add the conflicting words as another task answers the
      // conflict (the fixture reducer's rule, kept).
      if (op === "another" && meta.ideaId !== undefined) {
        delete state.conflicts[meta.ideaId];
      }
    },
    mutationSending(state, { payload: { key } }) {
      const e = state.pending[key];
      if (!e || e.status === "settled") return;
      e.status = "saving";
      e.applied = true;
    },
    mutationOffline(state, { payload: { key } }) {
      const e = state.pending[key];
      if (e && e.status !== "settled") e.status = "queued";
    },
    mutationSucceeded(state, { payload }) {
      const { key, data, clock } = payload;
      const entry = state.pending[key];
      if (!entry || !state.scope) return;
      if (clock) state.clock = clock;
      state.paused = false;
      if (entry.op === "claim") state.claimLost = {};
      if (entry.op === "finalize" && state.server) {
        // Personal facts arrive on the viewer's own response, never on the
        // squad channel (5.4): the completion latch and the cleared draft.
        const completed = data?.viewer?.plan_completed_at;
        if (completed) state.server.viewer.plan_completed_at = completed;
        // So is the quest the task became. The squad channel's finalize echo
        // carries no quest id, and when it lands first this response is not
        // newer, so the id is written here whatever the order (sandbox
        // re-check of PR #466: Go to Work named the previous task).
        const questId = data?.quest_id ?? data?.task?.quest_id;
        const locked = findById(state.server.tasks, entry.args.taskId);
        if (locked && questId !== null && questId !== undefined) {
          locked.quest_id = questId;
        }
        const drafts = state.server.viewer.definition_drafts;
        if (Array.isArray(drafts)) {
          state.server.viewer.definition_drafts = drafts.filter(
            (d) => !sameId(draftTaskId(d), entry.args.taskId),
          );
        }
      }
      const { message, entity } = responseAsEvent(state, entry, data);
      const v = versionOf(message);
      if (entry.visible && entity && !SESSION_OPS.has(entry.op) && v !== null) {
        // Keep the confirmed entity on the board until `server` holds a
        // version at least this one, so it never flickers out while a gap
        // refetch is in flight.
        state.pending[key] = {
          ...entry,
          status: "settled",
          result: entity,
          awaitVersion: v,
        };
      } else {
        delete state.pending[key];
      }
      ingest(state, message);
    },
    mutationFailed(state, { payload }) {
      const { key, error } = payload;
      const entry = state.pending[key];
      if (!entry) return;
      const code = error?.code;
      if (code === V6_PAUSED) {
        state.paused = true;
        entry.status = "failed";
        return;
      }
      if (code === "idea_already_converted" && entry.op === "convert") {
        state.conflicts[entry.args.ideaId] = {
          winnerTask: error.details?.winning_task || null,
          draftText: entry.draftText || entry.args.title,
          requestKey: key,
        };
      }
      if (code === "task_locked" && entry.op === "release") {
        state.releaseRefused[entry.args.taskId] = code;
      }
      if (code === "task_already_claimed" && entry.op === "claim") {
        const winner = error.details?.assignee_user_id;
        if (winner !== undefined && winner !== null) {
          state.claimLost[entry.args.taskId] = winner;
        }
      }
      if (error?.transient) {
        // Retries are exhausted: the card says "couldn't save" and Retry
        // resends with the same key.
        entry.status = "failed";
        return;
      }
      // A definitive refusal: this exact request can never succeed, so it is
      // not offered for retry, and the board reloads to show why (a teammate
      // got there first, the level was submitted, the version moved on).
      state.needsRefetch = true;
      delete state.pending[key];
    },
    /** The viewer's own definition draft, as the server stored it (D1). */
    draftStored(state, { payload: { taskId, draft, savedAt } }) {
      if (!state.server || !draft) return;
      if (typeof savedAt === "number") state.draftSavedAt[taskId] = savedAt;
      const drafts = Array.isArray(state.server.viewer.definition_drafts)
        ? state.server.viewer.definition_drafts
        : [];
      const next = { ...draft, task_id: draft.task_id ?? taskId };
      const i = drafts.findIndex((d) => sameId(draftTaskId(d), taskId));
      if (i === -1) drafts.push(next);
      else drafts[i] = next;
      state.server.viewer.definition_drafts = drafts;
    },
    conflictDiscarded(state, { payload: ideaId }) {
      delete state.conflicts[ideaId];
    },
    upgradeHandled(state) {
      state.upgradeRequired = false;
    },
  },
  extraReducers: (builder) => {
    // A legacy write refused because this session is now a v6 board (5.3).
    // The legacy thunks keep only `response.data.message` as their rejected
    // payload, so the code is looked for there. The v6 socket signal below is
    // the second route to the same flag.
    builder.addMatcher(
      (action) =>
        typeof action.type === "string" &&
        action.type.startsWith("planTasks/") &&
        action.type.endsWith("/rejected") &&
        typeof action.payload === "string" &&
        action.payload.includes(UPGRADE_REQUIRED),
      (state) => {
        state.upgradeRequired = true;
      },
    );
  },
});

export const {
  scopeSet,
  scopeCleared,
  fetchStarted,
  snapshotReceived,
  fetchFailed,
  boardEventReceived,
  refetchRequested,
  mutationQueued,
  mutationSending,
  mutationOffline,
  mutationSucceeded,
  mutationFailed,
  conflictDiscarded,
  draftStored,
  modeResolved,
  modeForgotten,
  pendingSettledOnClose,
  upgradeHandled,
} = planBoardSlice.actions;

export default planBoardSlice.reducer;

/* ---------------- socket entry points ---------------- */

const IDEA_UPSERT = new Set(["plan_idea_created", "plan_idea_updated"]);
const TASK_UPSERT = new Set([
  "plan_task_created",
  "plan_task_updated",
  "plan_task_claimed",
  "plan_task_unclaimed",
  "plan_task_finalized",
]);

/**
 * The squad channel's shape (the real B1 backend, contract correction
 * 2026-09-26): `{ type, session_id, squad_id, capsule_id, board_version,
 * data }`, where `data` is the entity or, for a delete, its ids. It becomes
 * the slice's own event form here, once, so every rule in `ingest` reads one
 * shape. `squad_id` and `capsule_id` arrive with B2; they are checked when
 * present.
 */
export function normalizeBoardEvent(raw) {
  const data = raw?.data && typeof raw.data === "object" ? raw.data : {};
  const m = {
    type: raw?.type,
    sessionId: raw?.session_id,
    squadId: raw?.squad_id,
    capsuleId: raw?.capsule_id,
    board_version: raw?.board_version,
  };
  // `data` is the mutation's own response body (PlanBoardService broadcast,
  // confirmed in the slice V sandbox): `{ idea | task | session, board_version,
  // server_now }`; deletes carry the ids themselves; finalize carries only
  // `{ task_id, locked, assignee_user_id }`.
  const obj = (v) => (v && typeof v === "object" ? v : null);
  if (IDEA_UPSERT.has(m.type)) {
    if (obj(data.idea)) m.idea = data.idea;
  } else if (m.type === "plan_idea_deleted") {
    m.ideaId = data.id ?? data.idea?.id;
  } else if (m.type === "plan_task_deleted") {
    m.taskId = data.id ?? data.task?.id;
    m.restored_idea_id = data.restored_idea_id;
  } else if (m.type === "plan_task_finalized" && data.task_id !== undefined) {
    // No task and no personal data: lock the task this board holds.
    m.taskId = data.task_id;
  } else if (TASK_UPSERT.has(m.type)) {
    if (obj(data.task)) m.task = data.task;
  } else if (
    m.type === "plan_round_started" ||
    m.type === "plan_round_skipped"
  ) {
    if (obj(data.session)) m.session = data.session;
  } else if (m.type === "plan_phase_changed") {
    if (obj(data.session)) {
      m.session = data.session;
      m.phase = data.session.phase;
    }
  }
  return m;
}

/**
 * A v6 board event from the squad channel. Events for no v6 board in view (a
 * legacy board is open, or none) raise the upgrade flag when they name the
 * legacy session being shown: that session has become a v6 board.
 */
export const planBoardSocketMessage = (raw) => (dispatch, getState) => {
  const message = normalizeBoardEvent(raw);
  const pb = getState().planBoard;
  if (pb?.scope) {
    dispatch(boardEventReceived(message));
    return;
  }
  const legacySessionId = getState().planTasks?.session?.id;
  if (
    legacySessionId !== undefined &&
    legacySessionId !== null &&
    sameId(message?.sessionId, legacySessionId)
  ) {
    dispatch(legacyUpgradeSignalled());
  }
};

/** The socket opened again: whatever was missed while it was down is refetched. */
export const planBoardReconnected = () => (dispatch, getState) => {
  if (getState().planBoard?.scope) dispatch(refetchRequested());
};

/* A plain action the slice answers with the same flag as a refused legacy write. */
export const legacyUpgradeSignalled = () => ({
  type: "planTasks/v6UpgradeSignal/rejected",
  payload: UPGRADE_REQUIRED,
});

/* ---------------- thunks ---------------- */

const perfNow = () =>
  typeof performance !== "undefined" && performance.now
    ? performance.now()
    : Date.now();

/**
 * The server clock at the moment a response arrived, from the request and
 * response midpoint (5.4). Advanced later with performance.now(), which is
 * monotonic, so a changed system clock cannot move the round.
 */
const clockFrom = (data, t0, t1) => {
  const server = toMs(data?.server_now);
  if (server === null) return null;
  return { serverMs: server + (t1 - t0) / 2, perfMs: t1 };
};

let warnedNoClock = false;
/**
 * The server's time now. Without a server clock (a response that carried no
 * `server_now`) the device clock is used, and that is said out loud once,
 * never silently (F1 review A6): a round could then read a little off.
 */
export const serverNow = (clock) => {
  if (clock) return clock.serverMs + (perfNow() - clock.perfMs);
  if (!warnedNoClock) {
    warnedNoClock = true;
    // eslint-disable-next-line no-console
    console.error("plan_board.server_clock_missing: using the device clock");
  }
  return Date.now();
};

const plainError = (error) => {
  const e = api.toPlanBoardError(error);
  return {
    status: e.status,
    code: e.code,
    message: e.message,
    details: e.details || {},
    transient: e.transient,
  };
};

const stillScoped = (getState, sessionId) =>
  sameId(getState().planBoard.scope?.sessionId, sessionId);

export const fetchBoard = () => async (dispatch, getState) => {
  const { scope, fetchInFlight } = getState().planBoard;
  if (!scope || fetchInFlight) return;
  const { sessionId } = scope;
  dispatch(fetchStarted());
  const t0 = perfNow();
  try {
    const data = await api.getBoard(sessionId);
    if (!stillScoped(getState, sessionId)) return;
    releaseStoppedDrafts(sessionId, data, getState().planBoard.boardVersion);
    dispatch(
      snapshotReceived({
        sessionId,
        data,
        clock: clockFrom(data, t0, perfNow()),
        at: Date.now(),
        // D4: when this request started, on the same clock as draft saves.
        requestStartedAt: t0,
      }),
    );
  } catch (error) {
    if (!stillScoped(getState, sessionId)) return;
    dispatch(fetchFailed({ sessionId, error: plainError(error) }));
  }
};

/**
 * Which board this student's Plan step shows (6, Mount). Resolves to
 *   { mode: "legacy" }                       the v6 route answered 404
 *                                            (plan_board_v6_disabled, or not
 *                                            deployed), or no squad
 *   { mode: "v6", sessionId }                a 5.2 snapshot; it is loaded
 *   { mode: "error", error }                 anything else, shown with Retry
 * Only a 404 means legacy. Every other failure is shown, never quietly turned
 * into the legacy board.
 */
export const resolvePlanBoardMode =
  ({ squadId, capsuleId, viewerId, signal }) =>
  async (dispatch) => {
    if (!squadId || !capsuleId) return { mode: "legacy" };
    let session;
    try {
      session = await api.getLegacySession(squadId, capsuleId, { signal });
    } catch (error) {
      const e = plainError(error);
      if (signal?.aborted) return { mode: "aborted" };
      return { mode: "error", error: e };
    }
    // The legacy reducer's own precedence (planTasks fetchPlanSession).
    const sessionId = (session?.session || session)?.id;
    if (sessionId === undefined || sessionId === null) {
      return { mode: "legacy" };
    }
    const t0 = perfNow();
    let data;
    try {
      data = await api.getBoard(sessionId, { signal });
    } catch (error) {
      if (signal?.aborted) return { mode: "aborted" };
      const e = plainError(error);
      if (e.status === 404) return { mode: "legacy" };
      return { mode: "error", error: e };
    }
    if (signal?.aborted) return { mode: "aborted" };
    if (!isSnapshot(data)) {
      return {
        mode: "error",
        error: { status: 200, code: "bad_snapshot", message: "" },
      };
    }
    dispatch(
      scopeSet({ sessionId: Number(sessionId), squadId, capsuleId, viewerId }),
    );
    dispatch(fetchStarted());
    dispatch(
      snapshotReceived({
        sessionId: Number(sessionId),
        data,
        clock: clockFrom(data, t0, perfNow()),
        at: Date.now(),
        // D4: the entry load uses the same one clock as fetchBoard.
        requestStartedAt: t0,
      }),
    );
    return { mode: "v6", sessionId: Number(sessionId) };
  };

const OPS = {
  addIdea: api.addIdea,
  editIdea: api.editIdea,
  deleteIdea: api.deleteIdea,
  convert: api.convertIdea,
  another: api.convertIdea,
  addTask: api.addTask,
  editTask: api.editTask,
  deleteTask: api.deleteTask,
  restore: api.restoreTask,
  claim: api.claimTask,
  release: api.releaseTask,
  startRound: api.startRound,
  skipRound: api.skipRound,
  openClaim: api.openClaim,
  finalize: api.finalizeTask,
};

export const RETRY_DELAYS_MS = [1000, 2000, 4000];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * Sends (or resends) the request held under `key`. A keyed operation always
 * sends the same `client_request_id`, so the server replays instead of doing
 * the work twice. Transient failures retry with the same key until a
 * definitive answer or the retries run out ("couldn't save", Retry).
 */
export const sendMutation = (key) => async (dispatch, getState) => {
  const first = getState().planBoard.pending[key];
  const sessionId = getState().planBoard.scope?.sessionId;
  if (!first || sessionId === undefined || sessionId === null) {
    return { ok: false, error: { code: "no_request" } };
  }
  if (isOffline()) {
    dispatch(mutationOffline({ key }));
    return { ok: false, queued: true };
  }
  dispatch(mutationSending({ key }));
  const run = OPS[first.op];
  const args = KEYED.has(first.op)
    ? { ...first.args, requestKey: key }
    : first.args;
  for (let attempt = 0; ; attempt += 1) {
    const t0 = perfNow();
    try {
      const data = await run(sessionId, args);
      if (!stillScoped(getState, sessionId)) return { ok: false, stale: true };
      dispatch(
        mutationSucceeded({ key, data, clock: clockFrom(data, t0, perfNow()) }),
      );
      return { ok: true, data };
    } catch (error) {
      if (!stillScoped(getState, sessionId)) return { ok: false, stale: true };
      const e = plainError(error);
      if (e.transient && isOffline()) {
        dispatch(mutationOffline({ key }));
        return { ok: false, queued: true, error: e };
      }
      if (!e.transient || attempt >= RETRY_DELAYS_MS.length) {
        dispatch(mutationFailed({ key, error: e }));
        return { ok: false, error: e };
      }
      await wait(RETRY_DELAYS_MS[attempt]);
      if (
        !stillScoped(getState, sessionId) ||
        !getState().planBoard.pending[key]
      ) {
        return { ok: false, stale: true };
      }
    }
  }
};

/** Records the request under its key, then sends it. */
export const submitMutation =
  ({ key, op, args, meta }) =>
  (dispatch, getState) => {
    if (!getState().planBoard.scope || !OPS[op]) return undefined;
    dispatch(mutationQueued({ key, op, args, meta }));
    return dispatch(sendMutation(key));
  };

/** Retry from the card: the SAME key (PlanBoard's onRetry). */
export const retryMutation = (key) => (dispatch, getState) => {
  const e = getState().planBoard.pending[key];
  if (!e || (e.status !== "failed" && e.status !== "queued")) return undefined;
  return dispatch(sendMutation(key));
};

/** Back online: everything made offline goes out, each with its own key. */
export const flushQueued = () => (dispatch, getState) => {
  Object.entries(getState().planBoard.pending).forEach(([key, e]) => {
    if (e.status === "queued") dispatch(sendMutation(key));
  });
};

/* ---------------- definition drafts (D1) ---------------- */

/*
 * One save at a time per task, and only the newest words: a burst of typing
 * becomes at most one request in flight and one waiting, so two saves never
 * race each other's `version`.
 */
const draftQueues = new Map();
/* Tasks whose drafts the server will never take again (the task locked, or
   the level was submitted): saving stops, and the board reloads to show why
   (F2 review A4, A5). */
// session:task -> the board version at which the stop was seen.
const draftsStopped = new Map();
const STOP_DRAFTS = new Set(["task_locked", "level_submitted"]);

/*
 * A stopped task becomes savable again when a snapshot shows it unlocked on
 * an open level: a hard-deleted quest unlocks the task and its owner may
 * define it again (ADR-013; F4 review A5).
 */
/*
 * Only a snapshot that is at least as new as the stop, and that the board
 * will actually apply (monotonic: not older than the board it holds), can
 * release it. A stale, out-of-order snapshot showing the task unlocked says
 * nothing about now (PR #464 review).
 */
function releaseStoppedDrafts(sessionId, data, knownVersion) {
  if (!draftsStopped.size || !isSnapshot(data) || data.level?.submitted) return;
  const v = versionOf(data);
  if (knownVersion !== null && knownVersion !== undefined && v < knownVersion) {
    return;
  }
  data.tasks.forEach((t) => {
    const k = `${sessionId}:${t.id}`;
    if (!t.locked && draftsStopped.has(k) && v >= draftsStopped.get(k)) {
      draftsStopped.delete(k);
    }
  });
}

async function putDraft(dispatch, getState, sessionId, taskId, job) {
  const current = draftFor(getState().planBoard, taskId);
  try {
    const data = await api.saveDefinitionDraft(sessionId, {
      taskId,
      fields: job.fields,
      deltaSeconds: job.deltaSeconds,
      version: current?.version ?? 1,
    });
    if (!stillScoped(getState, sessionId)) return { ok: false, stale: true };
    const draft = data?.definition_draft;
    if (draft && typeof draft === "object") {
      dispatch(draftStored({ taskId, draft, savedAt: perfNow() }));
    }
    return { ok: true };
  } catch (error) {
    if (!stillScoped(getState, sessionId)) return { ok: false, stale: true };
    const e = plainError(error);
    // Another tab saved first: the snapshot brings the current draft and its
    // version, and the next save writes the newest words over it.
    if (e.code === "stale_version" || STOP_DRAFTS.has(e.code)) {
      dispatch(refetchRequested());
    }
    return { ok: false, error: e };
  }
}

/**
 * Save the draft's words and the define seconds since the last save. Saves
 * for one task run one at a time; while one is in flight the newest words
 * wait, and their seconds ADD UP, so no time is lost to coalescing. A save
 * that fails carries its seconds into the next one.
 */
export const saveDraft =
  ({ taskId, fields, deltaSeconds }) =>
  (dispatch, getState) => {
    const sessionId = getState().planBoard.scope?.sessionId;
    if (sessionId === undefined || sessionId === null) {
      return Promise.resolve({ ok: false });
    }
    const k = `${sessionId}:${taskId}`;
    if (draftsStopped.has(k))
      return Promise.resolve({ ok: false, stopped: true });
    const q = draftQueues.get(k) || { running: null, next: null, carry: 0 };
    q.next = {
      fields,
      deltaSeconds:
        (q.next?.deltaSeconds || 0) + Math.max(0, deltaSeconds || 0),
    };
    draftQueues.set(k, q);
    if (q.running) return q.running;
    q.running = (async () => {
      let result = { ok: true };
      while (q.next) {
        const job = { ...q.next, deltaSeconds: q.next.deltaSeconds + q.carry };
        q.next = null;
        q.carry = 0;
        // eslint-disable-next-line no-await-in-loop
        result = await putDraft(dispatch, getState, sessionId, taskId, job);
        if (!result.ok && STOP_DRAFTS.has(result.error?.code)) {
          // The stop holds at least until the version that refused it.
          draftsStopped.set(
            k,
            Number(result.error?.details?.board_version) ||
              getState().planBoard.boardVersion ||
              0,
          );
          q.next = null;
          q.carry = 0;
        } else if (!result.ok && !result.stale) {
          q.carry = job.deltaSeconds;
        }
      }
      q.running = null;
      if (!q.carry) draftQueues.delete(k);
      return result;
    })();
    return q.running;
  };

/* ---------------- the board as PlanBoard reads it ---------------- */

/**
 * `server` plus the viewer's own requests, in the snapshot's field names. The
 * adapter (planBoard/adapter.js) turns this into PlanBoard's props.
 */
export function selectBoardView(pb) {
  if (!pb?.server) return null;
  const viewerId = pb.scope?.viewerId;
  const ideas = pb.server.ideas.map((i) => ({ ...i }));
  const tasks = pb.server.tasks.map((t) => ({ ...t }));
  const entries = Object.entries(pb.pending).sort(
    (a, b) => a[1].seq - b[1].seq,
  );

  let tail =
    Math.max(
      0,
      ...ideas.map((i) => Number(i.board_seq) || 0),
      ...tasks.map((t) => Number(t.board_seq) || 0),
    ) + 1;

  const liveFirstFor = (ideaId) =>
    tasks.find(
      (t) =>
        !t.deleted_at &&
        t.is_first_conversion &&
        sameId(t.source_idea_id, ideaId),
    );

  const shownPending = {};
  entries.forEach(([key, e]) => {
    if (e.status === "settled") {
      if (!e.result) return;
      if (IDEA_OPS.has(e.op)) {
        if (e.op === "deleteIdea") removeById(ideas, e.result.id);
        else upsert(ideas, { ...e.result });
      } else if (e.op === "deleteTask") {
        removeById(tasks, e.result.id);
      } else {
        upsert(tasks, { ...e.result });
      }
      return;
    }
    if (!e.visible) return;
    const idea =
      e.args?.ideaId !== undefined ? findById(ideas, e.args.ideaId) : null;
    switch (e.op) {
      case "addIdea":
        ideas.push({
          id: e.tempId,
          text: e.args.text,
          author_user_id: viewerId,
          board_seq: tail++,
          version: 1,
          pending: true,
        });
        shownPending[key] = {
          kind: "idea",
          status: e.status,
          ideaId: e.tempId,
          draftText: e.args.text,
        };
        return;
      case "addTask":
      case "another":
        tasks.push({
          id: e.tempId,
          title: e.args.title,
          created_by_user_id: viewerId,
          assigned_to_user_id: null,
          board_seq: tail++,
          source_idea_id: e.op === "another" ? e.args.ideaId : null,
          source_idea_text: e.op === "another" ? (idea?.text ?? null) : null,
          is_first_conversion: false,
          locked: false,
          pending: true,
        });
        shownPending[key] = {
          kind: e.op === "addTask" ? "direct" : "another",
          status: e.status,
          taskId: e.tempId,
          ideaId: e.op === "another" ? e.args.ideaId : undefined,
          draftText: e.args.title,
        };
        return;
      case "convert": {
        const won = liveFirstFor(e.args.ideaId);
        const applied = !won && Boolean(idea);
        if (applied) {
          tasks.push({
            id: e.tempId,
            title: e.args.title,
            created_by_user_id: viewerId,
            assigned_to_user_id: null,
            board_seq: idea.board_seq,
            source_idea_id: e.args.ideaId,
            source_idea_text: idea.text,
            is_first_conversion: true,
            locked: false,
            pending: true,
          });
        }
        shownPending[key] = {
          kind: "convert",
          status: e.status,
          ideaId: e.args.ideaId,
          taskId: applied ? e.tempId : undefined,
          draftText: e.args.title,
          applied,
        };
        return;
      }
      case "claim": {
        const task = findById(tasks, e.args.taskId);
        const applied =
          e.status !== "failed" &&
          task &&
          (task.assigned_to_user_id === null ||
            task.assigned_to_user_id === undefined);
        if (applied) task.assigned_to_user_id = viewerId;
        shownPending[key] = {
          kind: "claim",
          status: e.status,
          taskId: e.args.taskId,
          applied: Boolean(applied),
        };
        return;
      }
      case "release": {
        const task = findById(tasks, e.args.taskId);
        if (
          task &&
          e.status !== "failed" &&
          sameId(task.assigned_to_user_id, viewerId)
        ) {
          task.assigned_to_user_id = null;
        }
        shownPending[key] = {
          kind: "unclaim",
          status: e.status,
          taskId: e.args.taskId,
        };
        return;
      }
      case "editIdea": {
        const target = findById(ideas, e.args.ideaId);
        if (target && e.status !== "failed") target.text = e.args.text;
        return;
      }
      case "deleteIdea":
        if (e.status !== "failed") removeById(ideas, e.args.ideaId);
        return;
      case "editTask": {
        const target = findById(tasks, e.args.taskId);
        if (target && e.status !== "failed") target.title = e.args.title;
        return;
      }
      case "deleteTask":
        if (e.status !== "failed") removeById(tasks, e.args.taskId);
        return;
      default:
    }
  });

  return {
    session: pb.server.session,
    ideas,
    tasks,
    members: pb.server.members,
    deliverable: pb.server.deliverable,
    viewer: pb.server.viewer,
    level: pb.server.level,
    pending: shownPending,
  };
}
